/**
 * PATH: apps/quote/src/components/builder/panels/takeoff-assist/DocumentItemCard.tsx
 * PURPOSE: Document-extracted item card
 */

import { FileText, Plus, Loader2, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DocumentExtractedItem } from '@/lib/quoteApi'

export default function DocumentItemCard({
  item, onApply, isApplied, isApplying,
}: {
  item: DocumentExtractedItem; onApply: () => void; isApplied: boolean; isApplying: boolean
}) {
  return (
    <div className={cn(
      'p-3 rounded-lg border transition-all',
      isApplied
        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white line-clamp-2">{item.description}</p>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
            {item.quantity && item.unit && <span className="font-medium">{item.quantity} {item.unit}</span>}
            {item.location && <span>• {item.location}</span>}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <FileText className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] text-slate-400 truncate">
              {item.source_document}{item.source_page && `, p.${item.source_page}`}
            </span>
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded',
              item.confidence >= 80
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : item.confidence >= 60
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
            )}>
              {item.confidence}%
            </span>
          </div>
        </div>
        <button onClick={onApply} disabled={isApplied || isApplying}
          className={cn('p-2 rounded transition-colors', isApplied ? 'bg-green-600 text-white cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50')}
          title={isApplied ? 'Added to quote' : 'Add to quote'}>
          {isApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : isApplied ? <CheckCircle2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
    </div>
  )
}
